// tailorApi.jsx

const API_BASE_URL = "https://tailor.beezetsoftwaresolution.com/api";

/**
 * Helper function for making API calls.
 * @param {string} url - The full URL endpoint to fetch.
 * @param {Object} options - Fetch options (method, headers, body, etc.).
 * @returns {Promise<Object | Array>} The parsed JSON response.
 */
const fetchApi = async (url, options = {}) => {
    try {
        console.log("Fetching API:", url);
        const response = await fetch(url, options);
        if (!response.ok) {
            const errorBody = await response.text();
            try {
                const errorJson = JSON.parse(errorBody);
                // Throw specific error message from the API, or a generic status message
                throw new Error(errorJson.error || `HTTP error! Status: ${response.status}.`);
            } catch {
                // Handle non-JSON error bodies
                throw new Error(`HTTP error! Status: ${response.status}. Response: ${errorBody}`);
            }
        }
        const text = await response.text();
        // Handle potentially empty JSON response gracefully
        return text ? JSON.parse(text) : {};
    } catch (error) {
        console.error("API Fetch Error:", error);
        throw error;
    }
};

// ------------------------------------
// --- Customer API Endpoints ---
// ------------------------------------
export const getCustomers = () => {
    return fetchApi(`${API_BASE_URL}/costumers/get_customers.php`);
};
export const addCustomer = (customerData) => {
    return fetchApi(`${API_BASE_URL}/costumers/add_customers.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerData),
    });
};
export const updateCustomer = (customerData) => {
    return fetchApi(`${API_BASE_URL}/costumers/edit_customer.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerData),
    });
};

// ------------------------------------
// --- Employee API Endpoints ---
// ------------------------------------
export const getEmployees = () => {
    return fetchApi(`${API_BASE_URL}/employee/get_employees.php`);
};
export const addEmployee = (employeeData) => {
    return fetchApi(`${API_BASE_URL}/employee/add_employee.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(employeeData),
    });
};
export const updateEmployee = (employeeData) => {
    return fetchApi(`${API_BASE_URL}/employee/edit_employee.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(employeeData),
    });
};
export const getEmployeeDetails = (employeeId) => {
    console.warn("Using general fetch for specific employee details.");
    return getEmployees().then(employees => employees.find(e => e.id === employeeId));
};

// ------------------------------------
// --- Costume/Order/Fitting Endpoints ---
// ------------------------------------
export const getCostumes = () => {
    return fetchApi(`${API_BASE_URL}/costume/get_costume.php`);
};

/**
 * Updates a costume's details via API.
 * API POST: https://tailor.beezetsoftwaresolution.com/api/costume/edit_costume.php
 * @param {Object} costumeData - The costume object with updated fields. Must include 'costume_id'.
 */
export const updateCostume = (costumeData) => {
    console.log("Calling API to update costume details...", costumeData);
    // FIX: Changed check from 'id' to 'costume_id' to align with the API's requirement 
    // and the format sent from the component (CostumeListing.jsx).
    if (!costumeData.costume_id) {
        return Promise.reject(new Error("Costume ID (costume_id) is required for update."));
    }
    return fetchApi(`${API_BASE_URL}/costume/edit_costume.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(costumeData),
    });
};

export const addCostume = (costumeData) => {
    console.warn("Using placeholder for addCostume.");
    return Promise.resolve({ success: true, message: "Costume added successfully" });
};

/** * Fetches ALL orders from the list endpoint. 
 */
export const getAllOrders = async () => {
    console.log(`Fetching all orders from: ${API_BASE_URL}/order/get_orders.php`);
    return fetchApi(`${API_BASE_URL}/order/get_orders.php`);
};

/** * Gets a single order detail (kept for legacy/future use).
 */
export const getOrderDetails = async (orderId) => {
    console.warn(`Attempting to find order ${orderId} from the list API...`);
    const allOrders = await getAllOrders();
    // Ensure ID comparison is robust (API may return ID as string)
    const idString = String(orderId);
    return Array.isArray(allOrders) ? allOrders.find(order => String(order.id) === idString) : null;
};

/**
 * NEW: Generic function to update any field in the order header.
 * API POST: https://tailor.beezetsoftwaresolution.com/api/order/edit_orders.php
 * @param {Object} updateData - Object containing order_id and fields to update.
 */
export const updateOrderDetails = (updateData) => {
    console.log("Calling API to update order details...", updateData);
    return fetchApi(`${API_BASE_URL}/order/edit_orders.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
    });
};

/**
 * DEDICATED NEW: Update the order's status.
 * @param {number} orderId - The ID of the order.
 * @param {string} status - The new status (Processing, Trial, Completed, etc.).
 */
export const updateOrderStatus = (orderId, status) => {
    return updateOrderDetails({ order_id: orderId, status });
};

/**
 * DEDICATED NEW: Update the order's payment details.
 * @param {number} orderId - The ID of the order.
 * @param {number} total_amount - The new total amount.
 * @param {number} advance_payment - The new advance payment.
 */
export const updateOrderPayment = (orderId, total_amount, advance_payment) => {
    return updateOrderDetails({ order_id: orderId, total_amount, advance_payment });
};

export const addFitting = (fittingData) => {
    console.warn("Using placeholder for addFitting.");
    return Promise.resolve({ success: true, message: "Fitting added successfully" });
};

// Kept for backward compatibility if other files use 'updateOrder'
export const updateOrder = updateOrderDetails;